import { Suspense, lazy, useEffect, useState } from "react";
import { useLoaderPhase } from "@/features/mascot/useLoaderPhase";
import { LOADER_PHASES } from "@/features/mascot/mascot.types";
import { poseSource, preloadPose } from "@/features/mascot/mascot.poses";
import { useNetworkActivity } from "@/lib/networkActivity";

/**
 * The mascot pulls in GSAP and its rig chunk. This loader is mounted app-wide, so importing it
 * statically would put that on every route's critical path — including the landing page. The
 * loader deliberately paints nothing for the first 450ms, which is far longer than the chunk
 * needs to arrive, so deferring it costs nothing visible.
 */
const loadBunnyMascot = () =>
  import("@/features/mascot/BunnyMascot").then((m) => ({ default: m.BunnyMascot }));
const BunnyMascot = lazy(loadBunnyMascot);

export function GlobalApiLoader() {
  const { pendingCount, label, startedAt, runId } = useNetworkActivity();
  const [mascotReady, setMascotReady] = useState(false);
  // A run is "open" from the first request until the store's grace window closes; the phase
  // clock advances only while something is actually pending (a seam holds it steady).
  const { phase, celebrating } = useLoaderPhase(runId, startedAt, pendingCount > 0, startedAt !== null);

  // The shell is driven by the *phase*: nothing is painted until the 450ms threshold in
  // useLoaderPhase, which the overwhelming majority of requests resolve inside. The run stays
  // open across the gaps between sequential requests, so the phase sequence runs once per piece
  // of work rather than restarting in every seam.
  const showing = phase !== null;

  // Spend the loader's deliberately blank 450ms grace period fetching the rig instead of
  // waiting until the shell is already on screen. This affects only tracked, user-blocking work;
  // the landing page's background auth probe never opens this activity store.
  useEffect(() => {
    if (startedAt === null) return;
    setMascotReady(false);
    void loadBunnyMascot();
    preloadPose("idle");
  }, [runId, startedAt]);

  // Stay one pose ahead of the phase clock. The current artwork remains visible until the next
  // pose has decoded, but prefetching keeps that handoff out of the user's critical moment.
  useEffect(() => {
    if (phase === "starting") {
      preloadPose("gathering");
      preloadPose("happy-jump");
    } else if (phase === "gathering") {
      preloadPose("working");
    } else if (phase === "working") {
      preloadPose("almost-there");
    }
  }, [phase]);

  useEffect(() => {
    document.body.classList.toggle("api-loader-open", showing);
    return () => {
      document.body.classList.remove("api-loader-open");
    };
  }, [showing]);

  const caption = phase ? LOADER_PHASES.find((item) => item.id === phase)?.label : undefined;

  return (
    <div
      className={`api-loader-shell${showing ? " is-visible" : ""}`}
      aria-hidden={showing ? undefined : "true"}
      data-visible={showing ? "true" : "false"}
      data-phase={phase ?? "none"}
      data-pending-count={pendingCount}
    >
      <div className="api-loader-backdrop" />
      <div className="api-loader" role="status" aria-live="polite" aria-label="Loading BunnyCal">
        <div className="api-loader-stage" aria-hidden="true">
          <span className="api-loader-aura" />
          <span className="api-loader-orbit"><i /><i /><i /></span>
          <div className="api-loader-bob">
            <div className="api-loader-shadow" />
            <div className="api-loader-bunny" data-mascot-ready={mascotReady ? "true" : "false"}>
              {/* The 13KB pose is preloaded from index.html and is independent of React/GSAP.
                  It makes the loader complete at first paint on a genuinely cold connection;
                  the rig replaces the identical artwork only after decode. */}
              <span className="api-loader-mascot-poster">
                <img src={poseSource("idle")} width="400" height="400" alt="" decoding="async" />
              </span>
              {/* Keep one rig mounted across phases so its decode-aware crossfade can retain the
                  previous pose until the next image is ready. A changed animation rebuilds and
                  restarts the GSAP timeline; `done` plays once while the other phases loop. */}
              {showing && (
                <Suspense fallback={null}>
                  <BunnyMascot
                    animation={phase ?? "starting"}
                    loop={phase !== "done"}
                    onReady={() => setMascotReady(true)}
                  />
                </Suspense>
              )}
            </div>
          </div>
        </div>

        <div className="api-loader-word">
          <span className="api-loader-wm">Bunny<b>Cal</b></span>
          <span className="api-loader-track"><span className="api-loader-fill" /></span>
          {/* Two lines: the phase name carries the progress, the label carries the meaning.
              Without a label the second line is simply omitted rather than padded with filler. */}
          <span className="api-loader-cap">
            {caption}
            {!celebrating && <span className="api-loader-dots" />}
          </span>
          {label && !celebrating && <span className="api-loader-context">{label}</span>}
        </div>
      </div>
    </div>
  );
}
